/**
 * The Parts List toolbar: search, the Assigned/Unassigned and Warehouse
 * filters, the assign / upload / export actions and Rows per page.
 */
import { useFilterVisibility } from '../../../hooks/useFilterVisibility';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { PAGE_SIZE_OPTIONS } from '../../ui/pagination';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { Download, Loader2, Search, Upload, UserPlus } from 'lucide-react';

/** The Warehouse filter's "every warehouse" choice. */
export const ALL_WAREHOUSES = 'all';

interface PartRepositoryToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  filterAssigned: string;
  onFilterAssignedChange: (value: string) => void;
  warehouse: string;
  onWarehouseChange: (value: string) => void;
  /** CR-8188: warehouse codes; the Warehouse filter is hidden when there are none. */
  warehouses: string[];
  selectedCount: number;
  onAssignClick: () => void;
  onUploadClick: () => void;
  onExport: () => void;
  exporting: boolean;
  /** Nothing to export (no rows, or the list is still loading). */
  exportDisabled: boolean;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
}

export default function PartRepositoryToolbar({
  search, onSearchChange, filterAssigned, onFilterAssignedChange,
  warehouse, onWarehouseChange, warehouses, selectedCount, onAssignClick,
  onUploadClick, onExport, exporting, exportDisabled, pageSize, onPageSizeChange,
}: PartRepositoryToolbarProps) {
  // CR-4802: Warehouse follows the tenant's filter shape, as on every panel.
  const fv = useFilterVisibility();
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by part number or description..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterAssigned} onValueChange={onFilterAssignedChange}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Parts</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
          </SelectContent>
        </Select>
        {fv.warehouse && warehouses.length > 0 && (
          <Select value={warehouse} onValueChange={onWarehouseChange}>
            <SelectTrigger className="w-[170px]" aria-label="Warehouse">
              {/* A chosen code reads "Warehouse 01", not a bare "01". */}
              <SelectValue>
                {warehouse === ALL_WAREHOUSES ? 'All warehouses' : `Warehouse ${warehouse}`}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_WAREHOUSES}>All warehouses</SelectItem>
              {warehouses.map((code) => (
                <SelectItem key={code} value={code}>{code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {selectedCount > 0 && (
          <Button onClick={onAssignClick}>
            <UserPlus className="h-4 w-4 mr-2" />
            Assign Suppliers ({selectedCount})
          </Button>
        )}
        <Button variant="outline" onClick={onUploadClick}>
          <Upload className="h-4 w-4 mr-2" />
          Upload Assignments
        </Button>
        <Button variant="outline" onClick={onExport} disabled={exportDisabled || exporting}>
          {exporting
            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            : <Download className="h-4 w-4 mr-2" />}
          Export
        </Button>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-xs text-muted-foreground">Rows per page:</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="h-8 text-sm border border-border rounded-lg px-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {/* CR-8186: the same choices as the Rows control at the bottom,
              so a size picked in either one shows in both. */}
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
